import { useEffect, useRef, useState } from "react";

import { TURNSTILE_ACTION } from "@/components/apply/turnstile";

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      appearance: "interaction-only";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  const script = document.createElement("script");
  const pending = new Promise<TurnstileApi>((resolve, reject) => {
    const timeoutId = window.setTimeout(
      () => reject(new Error("Turnstile script timed out")),
      8_000,
    );
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.clearTimeout(timeoutId);
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile loaded without an API"));
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new Error("Turnstile script failed to load"));
    };
    document.head.append(script);
  });
  const retryable = pending.catch((error: unknown) => {
    scriptPromise = null;
    script.remove();
    throw error;
  });
  scriptPromise = retryable;
  return retryable;
}

export function TurnstileField({
  cycle,
  onTokenChange,
}: {
  cycle: number;
  onTokenChange: (token: string | null) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();
  const [verificationError, setVerificationError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteKey || !container.current) return;
    let disposed = false;
    let widgetId: string | null = null;
    setVerificationError(null);

    void loadTurnstile()
      .then((turnstile) => {
        if (disposed || !container.current) return;
        widgetId = turnstile.render(container.current, {
          sitekey: siteKey,
          action: TURNSTILE_ACTION,
          appearance: "interaction-only",
          callback: (token) => {
            setVerificationError(null);
            onTokenChange(token);
          },
          "expired-callback": () => {
            setVerificationError("Security verification expired. Complete it again.");
            onTokenChange(null);
          },
          "error-callback": () => {
            setVerificationError("Security verification could not load. Refresh and try again.");
            onTokenChange(null);
          },
        });
      })
      .catch(() => {
        if (disposed) return;
        setVerificationError("Security verification could not load. Refresh and try again.");
        onTokenChange(null);
      });

    return () => {
      disposed = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [cycle, onTokenChange, siteKey]);

  if (!siteKey) return null;
  return (
    <div
      className="grid gap-2"
      data-apply-turnstile
      role="group"
      aria-label="Security verification"
    >
      <div ref={container} />
      {verificationError && (
        <p role="alert" className="text-meta text-destructive">
          {verificationError}
        </p>
      )}
    </div>
  );
}
